/**
 * Environments Hook
 * Fetch and manage lab environment definitions
 */

import { useState, useCallback } from 'react';
import { apiGet, ApiError } from '../services/api';

export interface SanitizedEnvironment {
  id: string;
  name: string;
  description: string;
  category: string;
  estimatedCostPerHour: number;
}

interface UseEnvironmentsResult {
  environments: SanitizedEnvironment[];
  loading: boolean;
  error: string | null;
  fetchEnvironments: () => Promise<SanitizedEnvironment[]>;
  getEnvironmentById: (id: string) => SanitizedEnvironment | undefined;
}

/**
 * Hook for fetching and managing lab environment definitions
 */
export function useEnvironments(): UseEnvironmentsResult {
  const [environments, setEnvironments] = useState<SanitizedEnvironment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch all available environments
   */
  const fetchEnvironments = useCallback(async (): Promise<SanitizedEnvironment[]> => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiGet<{ success: boolean; data: SanitizedEnvironment[] }>('/environments');
      const data = response.data || [];
      setEnvironments(data);
      return data;
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to fetch environments';
      setError(message);
      console.error('Failed to fetch environments:', err);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get an environment by ID from the loaded environments
   */
  const getEnvironmentById = useCallback(
    (id: string): SanitizedEnvironment | undefined => {
      return environments.find((e) => e.id === id);
    },
    [environments]
  );

  return {
    environments,
    loading,
    error,
    fetchEnvironments,
    getEnvironmentById,
  };
}
